import { userFacingError } from '../utils/userFacingError';
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { AuthService } from '../services/authService';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

interface ResetFormData {
  password: string;
  passwordRepeat: string;
}

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userId = useMemo(() => searchParams.get('userId') || '', [searchParams]);
  const secret = useMemo(() => searchParams.get('secret') || '', [searchParams]);

  const [formData, setFormData] = useState<ResetFormData>({
    password: '',
    passwordRepeat: ''
  });
  const [errors, setErrors] = useState<Partial<ResetFormData>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const isLinkValid = Boolean(userId && secret);

  const handleInputChange = (field: keyof ResetFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ResetFormData> = {};

    if (!formData.password.trim()) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Passwort muss mindestens 8 Zeichen lang sein';
    }

    if (!formData.passwordRepeat.trim()) {
      newErrors.passwordRepeat = 'Bitte Passwort wiederholen';
    } else if (formData.passwordRepeat !== formData.password) {
      newErrors.passwordRepeat = 'Passwörter stimmen nicht überein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isLinkValid) {
      toast.error('Ungültiger Reset-Link');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await AuthService.confirmPasswordReset(userId, secret, formData.password);
      setIsDone(true);
      toast.success('Passwort erfolgreich zurückgesetzt.');
    } catch (error) {
      toast.error(userFacingError(error, 'Passwort-Reset fehlgeschlagen'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Neues Passwort setzen
          </h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            Bitte vergib ein neues Passwort.
          </p>
        </div>

        {!isLinkValid ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
            Der Reset-Link ist unvollständig oder ungültig.
          </div>
        ) : isDone ? (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 text-sm">
            Passwort wurde aktualisiert. Du kannst dich jetzt anmelden.
          </div>
        ) : (
          <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
            <Input
              label="Neues Passwort"
              type="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              error={errors.password}
            />

            <Input
              label="Passwort wiederholen"
              type="password"
              autoComplete="new-password"
              value={formData.passwordRepeat}
              onChange={(e) => handleInputChange('passwordRepeat', e.target.value)}
              error={errors.passwordRepeat}
            />

            <Button type="submit" className="w-full" loading={isLoading} disabled={isLoading}>
              Passwort speichern
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-gray-600">
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
            Zurück zur Anmeldung
          </Link>
        </p>
      </div>
    </div>
  );
};

